import React, { useState } from 'react';
import axios from 'axios';
import Split from 'react-split';
import Header from './components/Header';
import ChatInterface from './components/ChatInterface';
import SuperprofileResume from './components/SuperprofileResume';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
    ? '/api' 
    : (process.env.REACT_APP_API_URL || 'http://localhost:8000');

function App() {
  const [cvText, setCvText] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [superprofile, setSuperprofile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chatStarted, setChatStarted] = useState(false);

  // Extract user's first name from superprofile for personalized chat
  const getUserName = () => {
    if (!superprofile) return "Candidate";
    
    // Extract name from the superprofile
    const nameMatch = superprofile.match(/Name: ([^\n]+)/);
    if (nameMatch && nameMatch[1] && nameMatch[1] !== '...') {
      // Return just the first name
      return nameMatch[1].split(' ')[0];
    }
    
    return "Candidate";
  };

  // Function to analyze CV and start the chat
  const analyzeCv = async () => {
    if (!cvText.trim()) {
      setError("Please paste a CV/resume text to analyze.");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Make sure URL is properly constructed with /api prefix if needed
      const url = `${API_BASE_URL}/analyze-cv`.replace(/\/+/g, '/').replace(':/', '://');
      console.log(`Sending CV analysis request to: ${url}`);
      
      const response = await axios.post(url, {
        cvText: cvText
      });
      
      // Store session ID and initial superprofile
      setSessionId(response.data.sessionId);
      setSuperprofile(response.data.superprofile);
      
      // Add initial message from bot
      setMessages([{
        type: 'bot',
        content: response.data.message,
        options: response.data.options || []
      }]);
      
      // Start the chat interface
      setChatStarted(true);
    } catch (err) {
      console.error("Error analyzing CV:", err);
      setError(err.response?.data?.detail || "Failed to analyze CV. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Function to send a message in the chat
  const sendMessage = async (messageContent) => {
    if (!messageContent.trim() || !sessionId) {
      console.error("Cannot send message: No message content or sessionId!");
      return;
    }
    
    console.log(`Sending message with sessionId: ${sessionId}`);
    
    // Add user message to chat
    setMessages(prev => [...prev, {
      type: 'user',
      content: messageContent
    }]);
    
    setLoading(true);
    
    try {
      const url = `${API_BASE_URL}/chat`.replace(/\/+/g, '/').replace(':/', '://');
      console.log(`Sending message to: ${url} with sessionId: ${sessionId}`);
      
      const response = await axios.post(url, {
        sessionId: sessionId,
        message: messageContent
      });
      
      console.log("Chat response:", response.data);
      
      // Check if response contains expected data
      if (!response.data.message) {
        throw new Error("Invalid response from server: Missing message");
      }
      
      // Add bot response to chat with any options returned from API
      setMessages(prev => [...prev, {
        type: 'bot',
        content: response.data.message,
        options: response.data.options || [], // Store options with the message
        allowMultipleSelections: response.data.allowMultipleSelections || false
      }]);
      
      // Update superprofile with latest information
      setSuperprofile(response.data.superprofile);
      console.log("Updated superprofile:", response.data.superprofile);
    } catch (err) {
      console.error("Error sending message:", err);
      setError(err.response?.data?.detail || "Failed to send message. Please try again.");
      
      // Add error message to chat
      setMessages(prev => [...prev, {
        type: 'bot',
        content: "I'm having trouble responding right now. Let's try again."
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app">
      <Header />
      
      {!chatStarted ? (
        <div className="cv-input-container">
          <h2>Paste your resume to get started</h2>
          <p>Our AI assistant will analyze your CV and help build your profile through a conversation</p>
          
          <textarea
            value={cvText}
            onChange={(e) => setCvText(e.target.value)}
            placeholder="Paste your resume text here..."
            disabled={loading}
          />
          
          {error && <div className="error">{error}</div>}
          
          <div className="button-container">
            <button 
              className="analyze-button"
              onClick={analyzeCv}
              disabled={loading || !cvText.trim()}
            >
              {loading ? "Analyzing..." : "Analyze Resume & Start Chat"}
            </button>
          </div>
        </div>
      ) : (
        <Split 
          className="split-layout"
          sizes={[60, 40]}
          minSize={300}
          gutterSize={10}
          gutterAlign="center"
        >
          <div className="chat-container-panel">
            <ChatInterface
              sessionId={sessionId}
              messages={messages}
              onSendMessage={sendMessage}
              loading={loading}
              userName={getUserName()}
            />
          </div>
          
          <div className="resume-container-panel">
            <SuperprofileResume superprofile={superprofile} />
          </div>
        </Split>
      )}
    </div>
  );
}

export default App;